import React, { useState } from 'react';
import styled from 'styled-components';
import { getAccessToken, getNativeWebview, useUserInfoQuery } from '../react-queries/queries';
import { getAppInfo, purchaseSubscription } from '../api/native/NativeApis';
import LoadingOverlay from '../components/LoadingOverlay';
import { NoobookRole } from '../common/enums';
type ProductId = 'com.noobook.subs.personal.monthly' | 'com.noobook.subs.community.monthly' | 'com.noobook.subs.leader.monthly';

type MembershipPlan = {
  level: number;
  googleProductId: string;
  googlePlanId: string;
  appleProductId: ProductId;
  title: string;
  price: string;
  description: string;
  features: string[];
};

const PLANS: MembershipPlan[] = [
  {
    level: 1,
    googleProductId: 'noobook_membership',
    googlePlanId: 'personal',
    appleProductId: 'com.noobook.subs.personal.monthly',
    title: '개인형',
    price: '2,900원',
    description: '나만의 독서·필사 습관을 탄탄하게 기록하고 싶다면!',
    features: [
      '도서 무제한 등록 및 독서·필사 기록 관리',
      '책장 생성·관리, 독서 타이머, 일정 관리',
      '참여 이력 관리 및 전용 네임카드 생성',
    ],
  },
  {
    level: 2,
    googleProductId: 'noobook_membership',
    googlePlanId: 'community',
    appleProductId: 'com.noobook.subs.community.monthly',
    title: '참여형',
    price: '3,900원',
    description: '서평단 신청과 독서 모임 참여로 더 넓게 소통하고 싶다면',
    features: [
      '개인형의 모든 기본 기능 포함',
      '서평단 및 필사단 신청 권한',
      '독서 모임 참여 및 커뮤니티 게시판 이용',
      '앱 내 이벤트 참여 권한',
    ],
  },
  {
    level: 3,
    googleProductId: 'noobook_membership',
    googlePlanId: 'leader',
    appleProductId: 'com.noobook.subs.leader.monthly',
    title: '리더형',
    price: '7,900원',
    description: '독서 모임과 서평단을 주도적으로 개설·운영하고 싶다면',
    features: [
      '참여형의 모든 권한 무제한 이용',
      '서평단·필사단 직접 모집 및 신청자 취합 파일 제공',
      '모임 개설 무제한 (유료 모임 개설 가능) 회원 선정 및 모임 커뮤니티 자유 운영',
      '콘텐츠 상위 노출 수수료 할인 (월 1회, 3일 노출)',
      '앱 내 배너 제작 할인 (월 1회, 3일 노출)',
    ],
  },
];

const Page = styled.div`
  min-height: 100%;
  background: #eef2fb;
  padding: 20px 16px 32px;
  box-sizing: border-box;
`;

const Card = styled.div`
  background: #eef2fb;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PlanBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PlanHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const PlanHeader = styled.h3`
  margin: 0;
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
  color: #1a2a6c;
`;

const SubscribeButton = styled.button`
  flex-shrink: 0;
  margin-top: 2px;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background: #007AFF;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #0056CC;
  }
`;

const FeatureList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FeatureItem = styled.li`
  font-size: 13px;
  line-height: 1.5;
  color: #333;
`;

type Props = {
  onClose?: () => void;
};

const findCurrentPlan = (
  productId: string | null,
  planId: string | null,
): MembershipPlan | undefined => {
  if (!productId) {
    return undefined;
  }
  return PLANS.find(
    (plan) =>
      plan.appleProductId === productId ||
      (plan.googleProductId === productId && plan.googlePlanId === planId),
  );
};

const MembershipSubscriptionPage: React.FC<Props> = () => {
  const [purchasingLevel, setPurchasingLevel] = useState<number | null>(null);
  const isWebview = getNativeWebview();
  const { data: userInfo, refetch: refetchUserInfo } = useUserInfoQuery();
  const currentProductId = userInfo?.subs_productId ?? null;
  const currentPlanId = userInfo?.subs_planId ?? null;
  const currentStatus = userInfo?.subs_status ?? null;
  const currentPlan = findCurrentPlan(currentProductId, currentPlanId);
  const userRole = userInfo?.userRole ?? null;
  const isAdmin = userRole === NoobookRole.ADMIN;

  const handleSubscribe = async (plan: MembershipPlan) => {
    if (!isWebview || purchasingLevel !== null) {
      return;
    }
    setPurchasingLevel(plan.level);
    try {
      const { accessToken } = await getAccessToken();
      const appInfoResult = await getAppInfo();
      const os = appInfoResult?.data?.os?.toLowerCase() ?? '';
      const isAndroid = os === 'android';
      

      const productId = isAndroid ? plan.googleProductId : plan.appleProductId;
      const planId = isAndroid ? plan.googlePlanId : null;

      let upgrade: 'up' | 'down' | null = null;
      if (currentPlan && currentPlan.level !== plan.level && currentStatus != null && currentStatus.toLowerCase() !== 'expired') {
        upgrade = plan.level > currentPlan.level ? 'up' : 'down';
      }

      const result = await purchaseSubscription(
        accessToken || '',
        productId,
        planId,
        upgrade,
      );
      if (result?.result !== 0) {
        alert('구독 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.' + result?.result);
      }
    } catch (e) {
      console.error('purchaseSubscription error', e);
      alert('구독 요청 중 오류가 발생했습니다.');
    } finally {
      await refetchUserInfo();
      setPurchasingLevel(null);
    }
  };

  return (
    <Page>
      {purchasingLevel !== null && <LoadingOverlay />}
      <Card>
        {PLANS.map((plan) => {
          const isCurrentPlan = currentStatus?.toLowerCase() !== 'expired' && currentPlan?.level === plan.level;
          return (
            <PlanBlock key={plan.appleProductId}>
              <PlanHeaderRow>
                <PlanHeader>
                  {plan.title} ({plan.price}) : {plan.description}
                </PlanHeader>
                <SubscribeButton
                  type="button"
                  disabled={!isWebview || isAdmin || purchasingLevel !== null}
                  onClick={() => handleSubscribe(plan)}
                >
                  {isCurrentPlan ? '구독중' : '구독하기'}
                </SubscribeButton>
              </PlanHeaderRow>
              <FeatureList>
                {plan.features.map((feature) => (
                  <FeatureItem key={feature}>{feature}</FeatureItem>
                ))}
              </FeatureList>
            </PlanBlock>
          );
        })}
      </Card>
    </Page>
  );
};

export default MembershipSubscriptionPage;
